import type { ComponentProps } from "react";
import type Link from "next/link";

type LinkHref = ComponentProps<typeof Link>["href"];

export const ROUTE_TITLES: Record<string, string> = {
  "/": "Home",
  "/library": "Training",
  "/library/scorm-courses": "SCORM Courses",
  "/library/forms/alerts": "Submission Notifications",
  "/library/forms": "Forms",
  "/tasks": "Tasks",
  "/users": "Users",
  "/settings": "Settings",
  "/settings/brand": "Brand Settings",
  "/settings/language": "Language Settings",
  "/reporting": "Reporting",
  "/automations": "Automations",
  "/marketplace": "Marketplace",
  "/my-training": "My Training",
  "/my-profile": "My Profile",
  "/paths": "Paths",
  "/path-builder": "Path Builder",
  "/workflows": "Workflows",
  "/roles": "Roles",
  "/roles/departments": "Departments",
  "/roles/departments/[id]": "Departments",
  "/locations": "Locations",
  "/locations/franchises": "Franchises",
  "/locations/franchises/[id]": "Franchises",
  "/locations/brands": "Brands",
  "/locations/brands/[id]": "Brands",
  "/locations/regions": "Regions",
  "/locations/regions/[id]": "Regions",
  "/settings/tags": "Tags",
  "/messages": "Messages",
  "/access": "Access",
  "/knowledge-check": "Knowledge Check",
  "/guest-feedback": "Guest feedback",
  "/recommendations": "Recommendations",
  "/hr/compliance": "Compliance",
};

export function getPageTitle(
  pathname: string,
  query: Record<string, string | string[] | undefined>,
  operationsBundle = false,
): string {
  if (
    operationsBundle &&
    (pathname === "/library/forms" ||
      pathname === "/library/forms/audits" ||
      pathname === "/library/tasks" ||
      pathname === "/tasks")
  ) {
    return "Operations";
  }
  if (pathname === "/library/forms") {
    return query.type === "audits" ? "Audits" : "Checklists";
  }
  if (pathname === "/library") {
    if (query.type === "modules") return "Modules";
    if (query.type === "courses") return "Courses";
    if (query.type === "resources") return "Resources";
    if (query.type === "check-ins") return "Check-ins";
    return "All";
  }
  if (pathname === "/recommendations") {
    switch (query.status) {
      case "pending":
        return "Inbox";
      case "inProgress":
        return "In progress";
      case "measuring":
        return "Measuring impact";
      case "completed":
        return "Done";
      case "dismissed":
        return "Dismissed";
      default:
        return "Recommendations";
    }
  }
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname];
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length > 0) {
    const base = `/${segments[0]}`;
    if (ROUTE_TITLES[base]) return ROUTE_TITLES[base];
  }
  return "Dashboard";
}

export function getPageTitleHref(
  pathname: string,
  query: Record<string, string | string[] | undefined>,
): LinkHref | null {
  if (pathname.startsWith("/library/library-item/")) {
    return "/library";
  }
  if (pathname === "/locations/[id]") {
    return "/locations";
  }
  if (pathname === "/locations/franchises/[id]") {
    return "/locations/franchises";
  }
  if (pathname === "/locations/regions/[id]") {
    return "/locations/regions";
  }
  if (pathname === "/locations/brands/[id]") {
    return "/locations/brands";
  }
  if (pathname === "/users/[id]") {
    return "/users";
  }
  if (pathname === "/roles/[id]") {
    return "/roles";
  }
  if (pathname === "/roles/departments/[id]") {
    return "/roles/departments";
  }
  if (!query.libraryItemId) return null;
  const preservedQuery: Record<string, string | string[]> = {};
  for (const [key, value] of Object.entries(query)) {
    if (key === "libraryItemId" || value == null) continue;
    preservedQuery[key] = value;
  }
  if (pathname === "/library") {
    return { pathname: "/library", query: preservedQuery };
  }
  if (pathname === "/library/forms") {
    return { pathname: "/library/forms", query: preservedQuery };
  }
  return null;
}

/** Routes that render their own page-content title row (avoid duplicate default title). */
export const ROUTES_WITH_CUSTOM_PAGE_TITLE = new Set([
  "/",
  "/account-plan",
  "/library/library-item/[id]",
  "/tasks/[id]",
]);

export function shouldShowDefaultPageTitle(pathname: string): boolean {
  if (ROUTES_WITH_CUSTOM_PAGE_TITLE.has(pathname)) return false;
  if (pathname.startsWith("/library/library-item/")) return false;
  if (pathname.startsWith("/settings")) return false;
  if (pathname.startsWith("/guest-feedback/")) return false;
  return true;
}
